import GraphQLClient from 'ts/GraphQLClient'
import { RepoParams } from 'ts/types'
import { UserAccess, UserWithoutScope, UserWithScope } from '../sections/user-management/type'
import { Account, AccountMappingData, SaveAllocationParams } from '../types'
import { GET_ACCOUNT_MAPPINGS, GET_ACCOUNTS, REFETCH_ACCOUNTS, SAVE_ALLOCATION_QUERY } from './graphql'
import { GET_USERS_AND_ACCESS } from './graphql/getUsersAndAccess'
import { SAVE_USER_ACCESS_MUTATION } from './graphql/saverUserAccess'

class AdminRepo {
  private client: GraphQLClient

  constructor({ GRAPHQL_URL, GRAPHQL_API_KEY }: RepoParams) {
    this.client = new GraphQLClient({ url: GRAPHQL_URL, apiKey: GRAPHQL_API_KEY })
  }

  async getAccounts(): Promise<Account[]> {
    const data = await this.client.request<{ listAccounts: Account[] }>({
      query: GET_ACCOUNTS
    })

    return data.listAccounts || []
  }

  async getAccountMappings({ year }: { year: number }): Promise<AccountMappingData[]> {
    const data = await this.client.request<{
      listAccountMapping: { existingMappings: AccountMappingData[]; error?: { message: string } }
    }>({
      query: GET_ACCOUNT_MAPPINGS,
      variables: { year }
    })

    if (data.listAccountMapping.error) {
      throw new Error(data.listAccountMapping.error.message)
    }

    return data.listAccountMapping?.existingMappings || []
  }

  async saveAllocations({
    accountId,
    accountType,
    allocationYear,
    startDate,
    reference,
    allocations
  }: SaveAllocationParams): Promise<boolean> {
    const response = await this.client.request<{ createAccountMapping: { error?: { message: string } } }>({
      query: SAVE_ALLOCATION_QUERY,
      variables: {
        accountId,
        accountType,
        allocationYear,
        startDate,
        reference,
        allocations
      }
    })

    if (response.createAccountMapping.error) {
      throw new Error(response.createAccountMapping.error.message)
    }

    return true
  }

  async getUsersAndAccess({
    year
  }: {
    year: number
  }): Promise<{ usersWithScope: UserWithScope[]; usersWithoutScope: UserWithoutScope[] }> {
    const data = await this.client.request<{
      listUsers: UserWithoutScope[]
      listUserMapping: { error?: { message: string }; data?: UserWithScope[] }
    }>({
      query: GET_USERS_AND_ACCESS,
      variables: { year }
    })

    const { listUsers, listUserMapping } = data

    if (listUserMapping.error) {
      throw new Error(listUserMapping.error.message)
    }

    return {
      usersWithScope: listUserMapping.data || [],
      usersWithoutScope: listUsers
    }
  }

  async saveUserAccess({
    username,
    startDate,
    reference,
    effectiveYear,
    access
  }: {
    username: string
    startDate: string
    reference: string
    effectiveYear: number
    access: UserAccess[]
  }): Promise<boolean> {
    const response = await this.client.request<{ createUserMapping: { error?: { message: string } } }>({
      query: SAVE_USER_ACCESS_MUTATION,
      variables: {
        username,
        startDate,
        reference,
        effectiveYear,
        access
      }
    })

    if (response.createUserMapping.error) {
      throw new Error(response.createUserMapping.error.message)
    }

    return true
  }

  async reFetchAccounts(): Promise<{ new: Account[]; updated: Account[] }> {
    const { updateAccountsWithOrganizationsInfo } = await this.client.request<{
      updateAccountsWithOrganizationsInfo: { new: Account[]; updated: Account[] }
    }>({
      query: REFETCH_ACCOUNTS
    })

    return {
      new: updateAccountsWithOrganizationsInfo.new,
      updated: updateAccountsWithOrganizationsInfo.updated
    }
  }
}

export default AdminRepo
